import fetch from "cross-fetch";

import { VALID_REGEX } from "../../util/constants";
import { stringifyParams } from "../../util/stringifyParams";
import Singleton from "../Singleton";

import {
  CLCRT,
  CLCRT_FIELDS,
  DaperFinanceReferenceCriteria,
  DaperImageIdType,
  DaperStorySite,
  DaperStoryType,
} from "./enums";
import { DaperClientError } from "./Error";
import {
  DaperRecordBySlugParams,
  DaperFinanceReferenceParams,
  DaperFinanceResponse,
  DaperImagesBulkParams,
  DaperImagesBulkResponseObject,
  DaperLiveSchedItem,
  DaperLiveSchedParams,
  DaperLiveSchedResponse,
  DaperPersonResponse,
  DaperRecord,
  DaperRecordBySlugResponse,
  DaperSecuritiesSearchResponse,
  DaperSecuritySearchItem,
  DaperStorySearchLastPath,
  type DaperAudiosResponseObject,
  type DaperClientParams,
  type DaperErrorResponse,
  type DaperFinanceMoversParams,
  type DaperImagesResponse,
  type DaperImagesSearchParams,
  type DaperNewslettersResponse,
  type DaperSecurity,
  type DaperStoriesResponse,
  type DaperStory,
  type DaperStoryResponse,
  type DaperStorySearchParams,
  type DaperVideosResponse,
} from "./types";

class DaperClient extends Singleton {
  host: string;
  #username: string;
  #password: string;

  constructor(params: DaperClientParams) {
    super();

    if (!params.host) {
      throw new Error(`No host passed to DaperClient constructor; aborting`);
    }

    if (!params.username) {
      throw new Error(
        `No username passed to DaperClient constructor; aborting`
      );
    }

    if (!params.password) {
      throw new Error(
        `No password passed to DaperClient constructor; aborting`
      );
    }

    this.host = params.host;
    this.#username = params.username;
    this.#password = params.password;
  }

  getAuthHeader() {
    return {
      Authorization: `Basic ${btoa(`${this.#username}:${this.#password}`)}`,
    };
  }

  async getFinanceReferenceSecurities(
    params: DaperFinanceReferenceParams
  ): Promise<DaperSecurity[]> {
    this.validateGetFinanceReferenceSecurities(params);

    const stringifiedParams = stringifyParams(params);
    const url = `${this.host}/finance/reference?${stringifiedParams}&fields=${Object.keys(
      CLCRT
    ).join(",")}`;

    const response = await fetch(url, {
      headers: this.getAuthHeader(),
    });

    const data: DaperFinanceResponse = await response.json();

    if (!response.ok) {
      const error = data.error as DaperErrorResponse;

      throw new DaperClientError({
        errorResponse: error,
        status: response.status,
        url,
      });
    }

    return Object.entries(data.value.data).map(([id, v]) =>
      Object.entries(CLCRT_FIELDS).reduce(
        (acc, [k, clcrt]) => ({
          ...acc,
          [k]: v[clcrt],
          ticker: id,
        }),
        {} as DaperSecurity
      )
    );
  }

  async getFinanceMoversSecurities(params: DaperFinanceMoversParams) {
    this.validateGetFinanceMoversSecurities(params);

    const fields = Object.keys(CLCRT);
    const stringifiedParams = stringifyParams({ ...params, fields });
    const url = `${this.host}/finance/indices/${params.index}/movers?${stringifiedParams}`;
    const response = await fetch(url, {
      headers: this.getAuthHeader(),
    });

    const data: DaperFinanceResponse = await response.json();

    if (!response.ok) {
      const error = data.error as DaperErrorResponse;

      throw new DaperClientError({
        errorResponse: error,
        status: response.status,
        url,
      });
    }

    return Object.entries(data.value.data[params.criteria]).map(([id, v]) =>
      Object.entries(CLCRT_FIELDS).reduce(
        (acc, [k, clcrt]) => ({
          ...acc,
          [k]: v[clcrt],
          ticker: id,
        }),
        {} as DaperSecurity
      )
    );
  }

  async getLiveSched(
    params: DaperLiveSchedParams
  ): Promise<DaperLiveSchedItem[]> {
    this.validateGetLiveSched(params);

    const urlParams = stringifyParams({
      startTime: params.startTime,
      endTime: params.endTime,
    });
    const url = `${this.host}/livesched/shows/${params.channel}?${urlParams}`;

    const response = await fetch(url, {
      headers: this.getAuthHeader(),
    });

    const data: DaperLiveSchedResponse = await response.json();

    if (!response.ok) {
      const error = data.error as DaperErrorResponse;

      throw new DaperClientError({
        errorResponse: error,
        status: response.status,
        url,
      });
    }

    return data.value;
  }

  async getRecordBySlug(params: DaperRecordBySlugParams): Promise<DaperRecord> {
    this.validateGetRecordBySlug(params);
    const url = `${this.host}/records/${encodeURIComponent(params.slug)}`;
    const response = await fetch(url, {
      headers: this.getAuthHeader(),
    });
    const data: DaperRecordBySlugResponse = await response.json();
    if (!response.ok) {
      const error = data.error as DaperErrorResponse;

      throw new DaperClientError({
        errorResponse: error,
        status: response.status,
        url,
      });
    }

    return data.value;
  }

  async getAudiosBulk(params: { ids: string }) {
    this.validateGetAudiosBulk(params);

    const stringifiedParams = stringifyParams(params);
    const url = `${this.host}/audios/bulk?${stringifiedParams}`;

    const response = await fetch(url, {
      headers: this.getAuthHeader(),
    });

    const data: DaperAudiosResponseObject = await response.json();

    if (!response.ok) {
      const error = data.error as DaperErrorResponse;

      throw new DaperClientError({
        errorResponse: error,
        status: response.status,
        url,
      });
    }

    return data.value;
  }

  async getImagesBulk(params: DaperImagesBulkParams) {
    this.validateGetImagesBulk(params);

    const clonedParams = {
      ...params,
      ids: params.imageIds,
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const sanitizedParams = (({ imageIds, ...restOfParams }) => restOfParams)(
      clonedParams
    );

    const stringifiedParams = stringifyParams(sanitizedParams);
    const url = `${this.host}/images/bulk?${stringifiedParams}`;

    const response = await fetch(url, {
      headers: this.getAuthHeader(),
    });

    const data: DaperImagesBulkResponseObject = await response.json();

    if (!response.ok) {
      const error = data.error as DaperErrorResponse;

      throw new DaperClientError({
        errorResponse: error,
        status: response.status,
        url,
      });
    }

    return data.value;
  }

  async getVideoById(params: { id: string }) {
    this.validateGetVideoById(params);

    const url = `${this.host}/videos/${encodeURIComponent(params.id)}?idType=AVMM`;

    const response = await fetch(url, {
      headers: this.getAuthHeader(),
    });

    const data: DaperVideosResponse = await response.json();

    if (!response.ok) {
      const error = data.error as DaperErrorResponse;

      throw new DaperClientError({
        errorResponse: error,
        status: response.status,
        url,
      });
    }

    return data.value.video;
  }

  async getNewsletters() {
    const url = `${this.host}/newsletters`;

    const response = await fetch(url, {
      headers: this.getAuthHeader(),
    });

    const data: DaperNewslettersResponse = await response.json();

    if (!response.ok) {
      const error = data.error as DaperErrorResponse;

      throw new DaperClientError({
        errorResponse: error,
        status: response.status,
        url,
      });
    }

    return data.value.data;
  }

  async getNewslettersBySlug(params: { slug: string }) {
    this.validateGetNewslettersBySlug(params);

    const stringifiedParams = stringifyParams(params);
    const url = `${this.host}/newsletters?${stringifiedParams}`;

    const response = await fetch(url, {
      headers: this.getAuthHeader(),
    });

    const data: DaperNewslettersResponse = await response.json();

    if (!response.ok) {
      const error = data.error as DaperErrorResponse;

      throw new DaperClientError({
        errorResponse: error,
        status: response.status,
        url,
      });
    }

    return data.value.data;
  }

  async getPersonById(params: { id: string }) {
    this.validateGetPersonById(params);

    const url = `${this.host}/people/id/${params.id}`;

    const response = await fetch(url, {
      headers: this.getAuthHeader(),
    });

    const data: DaperPersonResponse = await response.json();

    if (!response.ok) {
      const error = data.error as DaperErrorResponse;

      throw new DaperClientError({
        errorResponse: error,
        status: response.status,
        url,
      });
    }

    return data.value;
  }

  async getThumbnailImagesByAVMMId(params: DaperImagesSearchParams) {
    this.validateGetThumbnailImagesByAVMMId(params);

    const stringifiedParams = stringifyParams(params);
    const url = `${this.host}/images/thumbnail/${params.id}?${stringifiedParams}`;

    const response = await fetch(url, {
      headers: this.getAuthHeader(),
    });

    const data: DaperImagesResponse = await response.json();

    if (!response.ok) {
      const error = data.error as DaperErrorResponse;

      throw new DaperClientError({
        errorResponse: error,
        status: response.status,
        url,
      });
    }

    return data.value.image;
  }

  async getSecuritiesSearchBy(params: {
    size: number;
    from: number;
  }): Promise<DaperSecuritySearchItem[]> {
    this.validateGetSecuritiesSearchBy(params);

    const url = `${this.host}/securities/search?size=${params.size}&from=${params.from}`;

    const response = await fetch(url, {
      headers: this.getAuthHeader(),
    });

    const data: DaperSecuritiesSearchResponse = await response.json();

    if (!response.ok) {
      const error = data.error as DaperErrorResponse;

      throw new DaperClientError({
        errorResponse: error,
        status: response.status,
        url,
      });
    }

    return data.value.hits;
  }

  async getSecuritiesTotalHits(params: {
    size: number;
    from: number;
  }): Promise<number> {
    this.validateGetSecuritiesSearchBy(params);

    const url = `${this.host}/securities/search?size=${params.size}&from=${params.from}`;

    const response = await fetch(url, {
      headers: this.getAuthHeader(),
    });

    const data: DaperSecuritiesSearchResponse = await response.json();

    if (!response.ok) {
      const error = data.error as DaperErrorResponse;

      throw new DaperClientError({
        errorResponse: error,
        status: response.status,
        url,
      });
    }

    return data.value.totalHits;
  }

  async getStoriesSearchBy(
    params: DaperStorySearchParams
  ): Promise<DaperStory[]> {
    this.validateGetStoriesSearchBy(params);

    const urlParams = stringifyParams(params);
    const url = `${this.host}/stories/search?${urlParams}`;

    const response = await fetch(url, {
      headers: this.getAuthHeader(),
    });

    const data: DaperStoriesResponse = await response.json();

    if (!response.ok) {
      const error = data.error as DaperErrorResponse;

      throw new DaperClientError({
        errorResponse: error,
        status: response.status,
        url,
      });
    }

    return data.value.items;
  }

  async getStoriesSearchLast(
    path: DaperStorySearchLastPath,
    params: DaperStorySearchParams
  ): Promise<DaperStory[]> {
    this.validateGetStoriesSearchLast(path, params);

    const urlParams = stringifyParams(params);
    const url = `${this.host}/stories/search/last/${path.field}/${path.period}/${path.units}?${urlParams}`;

    const response = await fetch(url, {
      headers: this.getAuthHeader(),
    });

    const data: DaperStoriesResponse = await response.json();

    if (!response.ok) {
      const error = data.error as DaperErrorResponse;

      throw new DaperClientError({
        errorResponse: error,
        status: response.status,
        url,
      });
    }

    return data.value.items;
  }

  async getStoriesBySlug(params: { slug: string }): Promise<DaperStory> {
    this.validateGetStoriesBySlug(params);

    const url = `${this.host}/stories/slug/${encodeURIComponent(params.slug)}`;

    const response = await fetch(url, {
      headers: this.getAuthHeader(),
    });

    const data: DaperStoryResponse = await response.json();

    if (!response.ok) {
      const error = data.error as DaperErrorResponse;

      throw new DaperClientError({
        errorResponse: error,
        status: response.status,
        url,
      });
    }

    return data.value;
  }

  async getStoryById(params: { id: string }): Promise<DaperStory> {
    this.validateGetStoryById(params);

    const url = `${this.host}/stories/id/${encodeURIComponent(params.id)}`;

    const response = await fetch(url, {
      headers: this.getAuthHeader(),
    });

    const data: DaperStoryResponse = await response.json();

    if (!response.ok) {
      const error = data.error as DaperErrorResponse;

      throw new DaperClientError({
        errorResponse: error,
        status: response.status,
        url,
      });
    }

    return data.value;
  }

  validateGetFinanceReferenceSecurities = (params: {
    securities: string[];
  }): boolean => {
    if (!params.securities || params.securities.length === 0) {
      throw new Error(
        "Missing required parameter for DaperClient.getFinanceReferenceSecurities: securities"
      );
    }

    params.securities.forEach((security) => {
      if (
        typeof security !== "string" ||
        !security.match(VALID_REGEX.security)
      ) {
        throw new Error(
          `Invalid security passed to DaperClient.getFinanceReferenceSecurities: ${security}`
        );
      }

      if (security.length > 20) {
        throw new Error(
          `Invalid security passed to DaperClient.getFinanceReferenceSecurities: ${security} exceeds length limit`
        );
      }
    });

    return true;
  };

  validateGetSecuritiesSearchBy = (params: {
    size: number;
    from: number;
  }): boolean => {
    if (params.size < 0 || typeof params.size !== "number") {
      throw new Error(
        "Missing required parameter for DaperClient.getSecuritiesSearchBy: size"
      );
    }

    if (params.from < 0 || typeof params.from !== "number") {
      throw new Error(
        "Missing required parameter for DaperClient.getSecuritiesSearchBy: from"
      );
    }

    const sizeLimit = 10000;

    if (params.size > sizeLimit) {
      throw new Error(
        `Invalid size passed to DaperClient.getSecuritiesSearchBy: ${params.size} exceeds size limit of 10000`
      );
    }

    return true;
  };

  validateGetFinanceMoversSecurities = (
    params: DaperFinanceMoversParams
  ): boolean => {
    if (!params.index || typeof params.index !== "string") {
      throw new Error(
        "Missing required parameter for DaperClient.getFinanceMoversSecurities: index"
      );
    }

    if (params.index.length > 20) {
      throw new Error(
        `Invalid index passed to DaperClient.getFinanceMoversSecurities: ${params.index} exceeds length limit`
      );
    }

    if (
      !params.criteria ||
      !Object.values(DaperFinanceReferenceCriteria).includes(params.criteria)
    ) {
      throw new Error(
        `Invalid criteria passed to DaperClient.getFinanceMoversSecurities: ${params.criteria}`
      );
    }

    return true;
  };
  validateGetRecordBySlug(params: DaperRecordBySlugParams): boolean {
    if (
      params.slug === null ||
      params.slug === undefined ||
      params.slug === ""
    ) {
      throw new Error(
        "Missing required parameter for DaperClient.getRecordBySlug: slug"
      );
    }

    if (typeof params.slug !== "string") {
      throw new Error(
        `Invalid parameter type for DaperClient.getRecordBySlug: slug. Expected: "string". Received: "${typeof params.slug}"`
      );
    }

    if (!params.slug.match(VALID_REGEX.slug)) {
      throw new Error(
        `Invalid slug passed to DaperClient.getRecordBySlug: "${params.slug}"`
      );
    }

    if (params.slug.length > 100) {
      throw new Error(
        `Invalid slug passed to DaperClient.getRecordBySlug: "${params.slug}" exceeds length limit`
      );
    }

    return true;
  }
  validateGetAudiosBulk = (params: { ids: string }): boolean => {
    if (!params.ids) {
      throw new Error(
        "Missing required parameter for DaperClient.getAudiosBulk: ids"
      );
    }

    if (typeof params.ids !== "string" || !params.ids.match(VALID_REGEX.ids)) {
      throw new Error(
        `Invalid ids passed to DaperClient.getAudiosBulk: ${params.ids}`
      );
    }

    if (params.ids.length > 200) {
      throw new Error(
        `Invalid ids passed to DaperClient.getAudiosBulk: ${params.ids} exceeds length limit`
      );
    }

    return true;
  };

  validateGetImagesBulk = ({ imageIds }: DaperImagesBulkParams): boolean => {
    if (!imageIds) {
      throw new Error(
        "Missing required parameter for DaperClient.getImagesBulk: imageIds"
      );
    }

    if (!Array.isArray(imageIds)) {
      throw new Error(
        `Invalid imageIds passed to DaperClient.getImagesBulk: imageIds must be an array. Non-array imageIds: ${imageIds}`
      );
    }

    if (!imageIds.every((id) => typeof id === "string")) {
      throw new Error(
        `Invalid imageIds passed to DaperClient.getImagesBulk: imageIds must be an array of string. Non-string imageId: ${imageIds.find((id) => typeof id !== "string")}`
      );
    }

    if (imageIds.length > 200) {
      throw new Error(
        `Invalid imageIds passed to DaperClient.getImagesBulk: ${imageIds} exceeds length limit`
      );
    }

    return true;
  };

  validateGetVideoById = (params: { id: string }): boolean => {
    if (!params.id) {
      throw new Error(
        "Missing required parameter for DaperClient.getVideoById: id"
      );
    }

    if (typeof params.id !== "string" || !params.id.match(VALID_REGEX.id)) {
      throw new Error(
        `Invalid id passed to DaperClient.getVideoById: ${params.id}`
      );
    }

    if (params.id.length > 100) {
      throw new Error(
        `Invalid id passed to DaperClient.getVideoById: ${params.id} exceeds length limit`
      );
    }

    return true;
  };

  validateGetNewslettersBySlug = (params: { slug: string }): boolean => {
    if (!params.slug) {
      throw new Error(
        "Missing required parameter for DaperClient.getNewslettersBySlug: slug"
      );
    }

    if (
      typeof params.slug !== "string" ||
      !params.slug.match(VALID_REGEX.slug)
    ) {
      throw new Error(
        `Invalid slug passed to DaperClient.getNewslettersBySlug: ${params.slug}`
      );
    }

    if (params.slug.length > 100) {
      throw new Error(
        `Invalid slug passed to DaperClient.getNewslettersBySlug: ${params.slug} exceeds length limit`
      );
    }

    return true;
  };

  validateGetPersonById = (params: { id: string }): boolean => {
    if (!params.id) {
      throw new Error(
        "Missing required parameter for DaperClient.getPersonById: id"
      );
    }

    if (!params.id.match(VALID_REGEX.id)) {
      throw new Error(
        `Invalid slug passed to DaperClient.getPersonById: ${params.id}`
      );
    }

    if (params.id.length > 100) {
      throw new Error(
        `Invalid slug passed to DaperClient.getPersonById: ${params.id} exceeds length limit`
      );
    }

    return true;
  };

  validateGetStoriesBySlug = (params: { slug: string }): boolean => {
    if (!params.slug) {
      throw new Error(
        "Missing required parameter for DaperClient.getStoriesBySlug: slug"
      );
    }

    if (
      typeof params.slug !== "string" ||
      !params.slug.match(VALID_REGEX.slug)
    ) {
      throw new Error(
        `Invalid slug passed to DaperClient.getStoriesBySlug: ${params.slug}`
      );
    }

    if (params.slug.length > 100) {
      throw new Error(
        `Invalid slug passed to DaperClient.getStoriesBySlug: ${params.slug} exceeds length limit`
      );
    }

    return true;
  };

  validateGetLiveSched = (params: { channel: string }): boolean => {
    if (!params.channel) {
      throw new Error(
        "Missing required parameter for DaperClient.getLiveSched: channel"
      );
    }

    if (typeof params.channel !== "string") {
      throw new Error(
        `Invalid channel passed to DaperClient.getLiveSched: ${params.channel}`
      );
    }

    return true;
  };

  validateGetStoryById = (params: { id: string }): boolean => {
    if (!params.id) {
      throw new Error(
        "Missing required parameter for DaperClient.getStoryById: id"
      );
    }

    if (typeof params.id !== "string" || !params.id.match(VALID_REGEX.id)) {
      throw new Error(
        `Invalid slug passed to DaperClient.getStoryById: ${params.id}`
      );
    }

    if (params.id.length > 100) {
      throw new Error(
        `Invalid slug passed to DaperClient.getStoryById: ${params.id} exceeds length limit`
      );
    }

    return true;
  };

  validateGetThumbnailImagesByAVMMId = (
    params: DaperImagesSearchParams
  ): boolean => {
    const lengthLimit = 200;

    if (!params.id) {
      throw new Error(
        `Missing required parameter in DaperClient.getThumbnailImagesByAVMMId: id`
      );
    }

    if (
      params.idType &&
      !Object.keys(DaperImageIdType).includes(params.idType)
    ) {
      throw new Error(
        `Invalid idType passed to DaperClient.getThumbnailImagesByAVMMId: ${params.idType}`
      );
    }

    Object.entries(params).forEach(([key, value]) => {
      if (key.length > lengthLimit) {
        throw new Error(
          `Invalid key passed to DaperClient.getThumbnailImagesByAVMMId: ${key} exceeds length limit`
        );
      }

      if (!key.toString().match(VALID_REGEX.objectKey)) {
        throw new Error(
          `Invalid key passed to DaperClient.getThumbnailImagesByAVMMId: ${key}`
        );
      }

      if (value.toString().length > lengthLimit) {
        throw new Error(
          `Invalid value passed to DaperClient.getThumbnailImagesByAVMMId: ${value} exceeds length limit`
        );
      }

      if (!value.toString().match(VALID_REGEX.objectValue)) {
        throw new Error(
          `Invalid value passed to DaperClient.getThumbnailImagesByAVMMId: ${value}`
        );
      }
    });

    return true;
  };

  validateGetStoriesSearchLast = (
    path: DaperStorySearchLastPath,
    params: DaperStorySearchParams
  ): boolean => {
    if (!path.field) {
      throw new Error(
        `No value passed for field to DaperClient.validateGetStoriesSearchLast`
      );
    }

    if (!path.period) {
      throw new Error(
        `No value passed for period to DaperClient.validateGetStoriesSearchLast`
      );
    }

    if (!path.units) {
      throw new Error(
        `No value passed for units to DaperClient.validateGetStoriesSearchLast`
      );
    }

    return this.validateGetStoriesSearchBy(params);
  };

  validateGetStoriesSearchBy = (params: DaperStorySearchParams): boolean => {
    const lengthLimit = 200;

    if (
      params.primarySites &&
      !params.primarySites.every((primarySite) =>
        Object.values(DaperStorySite).includes(primarySite)
      )
    ) {
      throw new Error(
        `Invalid site in primarySites passed to DaperClient.getStoriesSearchBy: ${params.primarySites}`
      );
    }

    if (
      params.type &&
      !params.type.every((storyType) =>
        Object.values(DaperStoryType).includes(storyType)
      )
    ) {
      throw new Error(
        `Invalid story type in type passed to DaperClient.getStoriesSearchBy: ${params.type}`
      );
    }

    Object.entries(params).forEach(([key, value]) => {
      if (key.length > lengthLimit) {
        throw new Error(
          `Invalid key passed to DaperClient.getStoriesSearchBy: ${key} exceeds length limit`
        );
      }

      if (!key.toString().match(VALID_REGEX.objectKey)) {
        throw new Error(
          `Invalid key passed to DaperClient.getStoriesSearchBy: ${key}`
        );
      }

      if (typeof value === "object" && !value) {
        throw new Error(
          `Invalid value passed to DaperClient.getStoriesSearchBy with ${key}: ${value}`
        );
      }

      if (typeof value !== "object") {
        if (value.toString().length > lengthLimit) {
          throw new Error(
            `Invalid value passed to DaperClient.getStoriesSearchBy: ${value} exceeds length limit`
          );
        }

        if (!value.toString().match(VALID_REGEX.objectValue)) {
          throw new Error(
            `Invalid value passed to DaperClient.getStoriesSearchBy: ${value}`
          );
        }
      }
    });

    return true;
  };
}

export default DaperClient;
